import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { Product, Variant } from 'shared/types/product';
import Link from 'components/commercetools-ui/atoms/link';
import Overlay from 'components/commercetools-ui/atoms/overlay';
import BrandImage from 'components/commercetools-ui/organisms/home-brand-refresh/brand-image';
import configLineItemExtractor from 'helpers/configLineItemExtractor/configLineItemExtractor';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import useScrollBlock from 'helpers/hooks/useScrollBlock';
import { useCart } from 'frontastic/hooks';
import { AddToCartOverlayContextShape, StateProduct, VariantData } from './types';

const AddToCartOverlayContext = React.createContext<AddToCartOverlayContextShape>({
  show() {},
  hide() {},
});

// Add-to-cart confirmation card: centered modal / mobile bottom sheet with an "Added to cart" heading,
// Continue Shopping / Go to Cart, and a product block that matches the brand
// cart drawer's line item — image on a grey tile with the name, quantity stepper and price stacked
// beside it. Driven by useCart() so the stepper acts on the line just added (matched by SKU +
// configuration).
const AddToCartOverlayProvider = ({ children }: React.PropsWithChildren) => {
  const { locale } = useI18n();
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { blockScroll } = useScrollBlock();
  const { data: cart, updateItem } = useCart();

  const [product, setProduct] = useState<StateProduct>();
  const [variantData, setVariantData] = useState<VariantData>();
  const [processing, setProcessing] = useState(false);

  const show = useCallback(
    (product: Product, variant: Variant, count: number, data?: VariantData) => {
      setProduct({ ...product, ...variant, count });
      setVariantData(data);
      blockScroll(true);
    },
    [blockScroll],
  );

  const hide = useCallback(() => {
    setProduct(undefined);
    setVariantData(undefined);
    // Clear the in-flight flag too, or a reopened overlay inherits a disabled stepper from an
    // update that was still running when the shopper dismissed the card.
    setProcessing(false);
    blockScroll(false);
  }, [blockScroll]);

  const configDetails = useMemo(() => {
    if (!product || !variantData?.config) return '';
    return configLineItemExtractor({ config: variantData.config } as ExtendedLineItem);
  }, [product, variantData]);

  // The cart line the shopper just added. The caller normally hands us its id, which is the only
  // reliable answer: builder (designId) and RoleModel lines share the template SKU and carry no
  // config, so the SKU + configuration search below compares all-undefined between two different
  // designs and resolves by array position. That search is the fallback for callers with no id.
  const addedLineItem = useMemo(() => {
    if (!product || !cart?.lineItems) return undefined;
    if (variantData?.lineItemId)
      return cart.lineItems.find((item) => item.lineItemId === variantData.lineItemId);
    const cfg = variantData?.config;
    return [...cart.lineItems]
      .reverse()
      .find(
        (item) =>
          item.variant?.sku === product.sku &&
          item.config?.finish === cfg?.finish &&
          item.config?.sheen === cfg?.sheen &&
          item.config?.wirebrush === cfg?.wirebrush,
      );
  }, [product, cart?.lineItems, variantData]);

  const lineItemId = addedLineItem?.lineItemId;
  const customLineItemId = addedLineItem?.customLineItemId ?? variantData?.customLineItemId;
  const count = addedLineItem?.count ?? product?.count ?? 1;
  // This slot means one thing: the line total. Every fallback below is a unit price, so scale it by
  // the count on display — unlabelled, a unit price reads as the amount just added to the cart.
  const unitPrice = variantData?.price ?? product?.discountedPrice ?? product?.price;
  const displayPrice =
    addedLineItem?.totalPrice ?? (unitPrice ? { ...unitPrice, centAmount: (unitPrice.centAmount ?? 0) * count } : {});

  // The stepper sends an absolute count derived from `count`, which only advances once the cart
  // round trip lands — so without this guard four fast clicks all read the same count and all send
  // the same next value. Mirrors the cart row's `processing` guard.
  const changeQuantity = async (nextCount: number) => {
    if (!lineItemId || processing) return;

    setProcessing(true);
    try {
      await updateItem?.(lineItemId, customLineItemId, Math.max(1, nextCount));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      {product && <Overlay onClick={hide} zIndex={999} />}
      <Transition
        show={!!product}
        /* @ts-ignore it actually works and it applies the className */
        className="fixed bottom-0 z-[9999] w-full overflow-hidden rounded-[20px_20px_0_0] bg-white shadow md:bottom-[unset] md:left-1/2 md:top-1/2 md:w-[90%] md:max-w-[650px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-none md:border md:border-stroke-primary md:shadow-none"
        enter="transition md:transition-opacity duration-75"
        enterFrom="opacity-0 translate-y-full"
        enterTo="opacity-100 translate-y-0"
        leave="transition md:transition-opacity duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-full"
      >
        <div onMouseUp={(e) => e.stopPropagation()}>
          <div className="bg-white p-24 font-gt md:p-40">
            <CloseIcon
              className="absolute right-20 top-22 size-24 cursor-pointer text-[#212121] md:right-32 md:top-32"
              onClick={hide}
            />

            <h4 className="pr-40 text-20 font-medium leading-[1.2] text-[#212121]">
              {formatProductMessage({ id: 'cart.added', defaultMessage: 'Added to cart' })}
            </h4>

            {/* Product card — matches the cart drawer's line item */}
            <div className="mt-24 flex gap-16">
              <BrandImage
                src={product?.images?.[0] ?? ''}
                alt={product?.name ?? ''}
                width={200}
                className="size-99 shrink-0 bg-[#f0f0f0] object-cover"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-12">
                <div>
                  <p className="text-15 text-[#212121]">{product?.name}</p>
                  {!!configDetails && <p className="mt-2 text-12 text-neutral-black-400">{configDetails}</p>}
                </div>
                <div className="flex w-fit items-center border border-stroke-primary">
                  <button
                    aria-label="Decrease quantity"
                    disabled={processing || !lineItemId}
                    onClick={() => changeQuantity(count - 1)}
                    className="flex size-32 items-center justify-center transition-colors hover:bg-[#f0f0f0] disabled:opacity-40"
                  >
                    <MinusIcon className="size-14" />
                  </button>
                  <span className="w-40 text-center text-15">{count}</span>
                  <button
                    aria-label="Increase quantity"
                    disabled={processing || !lineItemId}
                    onClick={() => changeQuantity(count + 1)}
                    className="flex size-32 items-center justify-center transition-colors hover:bg-[#f0f0f0] disabled:opacity-40"
                  >
                    <PlusIcon className="size-14" />
                  </button>
                </div>
                <p className="text-15 font-bold text-[#212121]">{CurrencyHelpers.formatForCurrency(displayPrice, locale)}</p>
              </div>
            </div>

            {/* Card footer — unchanged */}
            <div className="mt-24 flex w-full flex-col gap-12 md:mt-32 md:flex-row">
              <button
                type="button"
                onClick={hide}
                className="flex h-44 items-center justify-center border border-[#1c1c1c] bg-white text-16 font-medium text-[#1c1c1c] transition-colors hover:bg-[#f0f0f0] md:flex-1"
              >
                {formatCartMessage({ id: 'continue.shopping', defaultMessage: 'Continue shopping' })}
              </button>
              <Link link="/cart" onClick={hide} className="md:flex-1">
                <span className="flex h-44 items-center justify-center bg-[#1c1c1c] text-16 font-medium text-white transition-opacity hover:opacity-90">
                  {formatCartMessage({ id: 'cart.go', defaultMessage: 'Go to cart' })}
                </span>
              </Link>
            </div>
          </div>
        </div>
      </Transition>
      <AddToCartOverlayContext.Provider value={{ show, hide }}>{children}</AddToCartOverlayContext.Provider>
    </>
  );
};

export default AddToCartOverlayProvider;

export const useAddToCartOverlay = () => useContext(AddToCartOverlayContext);
